import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { api } from "../lib/api";
import { RangePresetSwitcher } from "../components/fleet-calendar/RangePresetSwitcher";
import { RANGE_PRESETS, rangeFromPreset } from "../lib/fleetCalendar";
import { Car, CalendarOff } from "lucide-react";

// A dedicated, simpler read model rather than retrofitting Fleet Calendar's
// CalendarGrid: that grid is built around vehicle status blocks/conflict
// types that don't map onto driver leave/suspension. This renders one row
// per driver with their assignments + availability blocks as a sorted chip
// list, not a pixel-positioned day grid: readable at small fleet sizes
// without inventing a second calendar-rendering engine.

const BLOCK_LABELS = {
  day_off: "Day off", sick_leave: "Sick leave", vacation: "Vacation",
  unavailable: "Unavailable", suspended: "Suspended",
};

function fmtRange(startIso, endIso) {
  try {
    const s = parseISO(startIso), e = parseISO(endIso);
    const sameDay = format(s, "yyyy-MM-dd") === format(e, "yyyy-MM-dd");
    return sameDay
      ? `${format(s, "MMM d, h:mm a")} – ${format(e, "h:mm a")}`
      : `${format(s, "MMM d, h:mm a")} – ${format(e, "MMM d, h:mm a")}`;
  } catch {
    return `${startIso} – ${endIso}`;
  }
}

function DriverStatusDot({ status }) {
  const tone = status === "active" ? "bg-emerald-500"
    : status === "suspended" ? "bg-red-500"
    : status === "unavailable" ? "bg-amber-500"
    : "bg-slate-400";
  return <span className={`inline-block w-2 h-2 rounded-full ${tone}`} title={status} />;
}

export default function DriverCalendar() {
  const [preset, setPreset] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { start, end } = rangeFromPreset(preset);
    setLoading(true);
    api.get("/drivers/calendar", { params: { start: start.toISOString(), end: end.toISOString() } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [preset]);

  return (
    <div className="space-y-6" data-testid="driver-calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Driver Calendar</h1>
          <p className="text-slate-600 mt-1 text-sm">Assignments and availability blocks per driver.</p>
        </div>
        <RangePresetSwitcher value={preset} onChange={setPreset} />
      </div>

      {loading && <div className="text-slate-500" data-testid="driver-calendar-loading">Loading…</div>}

      {!loading && data && data.drivers.length === 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">
          No drivers yet. Add driver capability to a staff member under Employees.
        </div>
      )}

      {!loading && data && data.drivers.length > 0 && (
        <div className="space-y-3">
          {data.drivers.map((row) => {
            const events = [
              ...row.assignments.map((b) => ({ type: "assignment", at: b.start_datetime, data: b })),
              ...row.blocks.map((blk) => ({ type: "block", at: blk.start_at, data: blk })),
            ].sort((a, b) => (a.at || "").localeCompare(b.at || ""));
            return (
              <div key={row.driver.id} className="rounded-xl border border-slate-200 bg-white p-4"
                   data-testid={`driver-cal-row-${row.driver.id}`}>
                <div className="flex items-center gap-2 mb-3">
                  <DriverStatusDot status={row.driver.driver_status} />
                  <Link to={`/app/drivers/${row.driver.id}`} className="font-semibold hover:underline">
                    {row.driver.driver_display_name || row.driver.name}
                  </Link>
                  <span className="text-xs text-slate-500 capitalize">{row.driver.driver_status || "-"}</span>
                </div>
                {events.length === 0 ? (
                  <p className="text-sm text-slate-400">No assignments or blocks in this range.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {events.map((ev, i) => ev.type === "assignment" ? (
                      <Link key={`a-${i}`} to={`/app/bookings/${ev.data.id}`}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800 hover:bg-blue-100"
                            data-testid="driver-cal-assignment">
                        <Car className="w-3.5 h-3.5" />
                        {fmtRange(ev.data.start_datetime, ev.data.end_datetime)} · {ev.data.customer_name || ev.data.booking_reference}
                      </Link>
                    ) : (
                      <span key={`b-${i}`}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-100 border border-slate-200 text-xs text-slate-700"
                            data-testid="driver-cal-block">
                        <CalendarOff className="w-3.5 h-3.5" />
                        {fmtRange(ev.data.start_at, ev.data.end_at)} · {BLOCK_LABELS[ev.data.block_type] || ev.data.block_type}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
